import { create } from 'zustand';

/**
 * ч.1397 — СЕСІЯ У httpOnly-COOKIE (власник після аудиту безпеки ч.1394: «действуй»).
 *
 * До ч.1397 access- і refresh-токени лежали у localStorage: будь-який XSS міг їх прочитати
 * і працювати від імені користувача з іншого компʼютера. Тепер токени живуть у cookie з
 * HttpOnly, які ставить сервер; скрипт сторінки їх не бачить і не зберігає. У сховищі —
 * лише ОЗНАКА сесії (`sessionActive`), ідентифікатор користувача (ключ сховищ налаштувань)
 * і активний клієнт. Запити авторизуються cookie автоматично (`api/client.ts`:
 * `withCredentials`), захист від міжсайтових запитів — заголовок `X-CSRF-Token` з
 * cookie `oblik_csrf` (без HttpOnly — її якраз і треба читати).
 */
export interface AuthState {
  /** Користувач увійшов (cookie-сесія на сервері). */
  sessionActive: boolean;
  /** Ідентифікатор користувача з відповіді входу/оновлення — ключ сховищ (uiPrefs тощо). */
  userId: string | null;
  /**
   * Поточний tenant context. Зберігається у localStorage окремо від сесії,
   * бо JWT не містить tenant_id (Порція 3.0.2: tenant контекст — header).
   *
   * - `null` коли user щойно залогінився і ще не обрав tenant (бачить
   *   /select-tenant) або не має жодного membership (бачить /no-tenants).
   * - Виставляється: успішний login → перенаправлення → user обирає tenant
   *   у /select-tenant; tenant switcher у хедері; accept-invitation;
   *   create-tenant з /no-tenants.
   * - Скидається: logout, помилка «No active membership» з backend, явний
   *   перехід на /select-tenant.
   */
  activeTenantId: string | null;
  setSession: (userId: string | null) => void;
  setActiveTenant: (tenantId: string | null) => void;
  clear: () => void;
}

const SESSION_KEY = 'auth.session';
const USER_KEY = 'auth.user_id';
const TENANT_KEY = 'auth.active_tenant_id';
/** Ключі до ч.1397 (токени у localStorage) — стираються при виході й при міграції сесії. */
export const LEGACY_TOKEN_KEYS = ['auth.access', 'auth.refresh'] as const;
export const CSRF_COOKIE = 'oblik_csrf';

export const useAuth = create<AuthState>((set) => ({
  sessionActive: localStorage.getItem(SESSION_KEY) === '1',
  userId: localStorage.getItem(USER_KEY),
  activeTenantId: localStorage.getItem(TENANT_KEY),
  setSession: (userId) => {
    localStorage.setItem(SESSION_KEY, '1');
    if (userId) localStorage.setItem(USER_KEY, userId);
    set((s) => ({ sessionActive: true, userId: userId ?? s.userId }));
  },
  setActiveTenant: (tenantId) => {
    if (tenantId === null) {
      localStorage.removeItem(TENANT_KEY);
    } else {
      localStorage.setItem(TENANT_KEY, tenantId);
    }
    set({ activeTenantId: tenantId });
  },
  clear: () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TENANT_KEY);
    for (const k of LEGACY_TOKEN_KEYS) localStorage.removeItem(k);
    set({ sessionActive: false, userId: null, activeTenantId: null });
  },
}));

/** Значення cookie, доступної скрипту (лише `oblik_csrf` така). */
export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [k, ...rest] = part.trim().split('=');
    if (k === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

export const readCsrfToken = (): string | null => readCookie(CSRF_COOKIE);

/**
 * Клієнт для НАСТУПНОГО завантаження сторінки (ч.1068) — стан живої сторінки
 * НЕ змінюємо. Перемикання клієнта = повне перезавантаження, але до
 * вивантаження сторінка ще живе десятки-сотні мс: якби активний клієнт
 * змінився одразу, відкладені записи (чернетки, вкладки) лягли б під ключ
 * НОВОГО клієнта, а повторні запити пішли б з його X-Tenant-Id на адреси
 * записів попереднього. Так уся стара сторінка до кінця живе в одному клієнті.
 */
export function setActiveTenantOnNextLoad(tenantId: string): void {
  localStorage.setItem(TENANT_KEY, tenantId);
}

/** Повна навігація (нова сторінка замість поточної). Обʼєктом — щоб тести могли підмінити. */
export const hardNav = {
  replace: (url: string): void => { window.location.replace(url); },
};

/**
 * ч.1229 — ОДНЕ ЖИТТЯ СТОРІНКИ = ОДИН КОРИСТУВАЧ І ОДИН КЛІЄНТ.
 *
 * Вхід у клієнта на живій сторінці (вибір акаунта після входу, прийняте запрошення,
 * щойно створений акаунт) — ЛИШЕ з повним перезавантаженням, як і перемикання клієнта у
 * шапці (`switchTenant`). Причина (баг власника: «змінюю клієнта — поле організації порожнє
 * і помилка»): сховища з ключем по клієнту (організація і період за замовчуванням, відкриті
 * вкладки) і кеш запитів читаються ОДИН раз, при завантаженні сторінки. `setActiveTenant(id)`
 * + перехід усередині застосунку лишав їх від попереднього клієнта: у шапці стояла його
 * організація, якої в новому клієнті немає, — порожнє поле, а запити з нею — помилка.
 *
 * З інтерфейсу `setActiveTenant` викликається лише з `null` (скинути клієнта); сторож —
 * `src/test/tenant_entry_invariants.test.ts`.
 */
export function enterTenant(tenantId: string, url = '/'): void {
  setActiveTenantOnNextLoad(tenantId);
  hardNav.replace(url);
}

/**
 * ч.1397: відкликати сесію на сервері й стерти httpOnly-cookie — їх зі скрипта не прибрати.
 * `keepalive` — запит доживе і після перезавантаження сторінки. Обʼєктом — для підміни в тестах.
 */
export const sessionApi = {
  revokeOnServer: (): void => {
    if (typeof fetch !== 'function') return;
    const csrf = readCsrfToken();
    try {
      void fetch('/api/v1/auth/logout', {
        method: 'POST', credentials: 'include', keepalive: true,
        headers: { 'X-Session': 'cookie', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
      }).catch(() => undefined);
    } catch { /* вихід однаково */ }
  },
};

/**
 * Вихід із сеансу — теж з повним перезавантаженням: у памʼяті сторінки не лишається ні кешу
 * запитів, ні сховищ, ні зчитаного ключа КЕП попереднього користувача (до ч.1229 вихід був
 * переходом усередині застосунку — наступний користувач у тій самій вкладці починав з його
 * станом). ч.1397: спершу відкликаємо сесію на сервері (cookie стираються там).
 */
export function leaveSession(url = '/login'): void {
  sessionApi.revokeOnServer();
  useAuth.getState().clear();
  hardNav.replace(url);
}

/** Ідентифікатор користувача поточної сесії — ключ сховищ налаштувань (uiPrefs). */
export function currentUserId(): string | null {
  return useAuth.getState().userId;
}

/**
 * Повернення «назад» зі збереженої браузером сторінки (bfcache): якщо тим
 * часом клієнта змінили (тут або в іншій вкладці) — сторінка з памʼяті
 * показувала б попереднього клієнта. Перезавантажуємо (ч.1068).
 */
export function reloadIfTenantChangedOnRestore(
  e: { persisted: boolean },
  reload: () => void = () => window.location.reload(),
): void {
  if (!e.persisted) return;
  if (localStorage.getItem(TENANT_KEY) !== useAuth.getState().activeTenantId) reload();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (e) => reloadIfTenantChangedOnRestore(e));
}

// Helper-функції для використання поза React-компонентами (api-клієнт).
export const isLoggedIn = () => useAuth.getState().sessionActive;
export const getUserId = () => useAuth.getState().userId;
export const getActiveTenantId = () => useAuth.getState().activeTenantId;
export const setSession = (userId: string | null | undefined) =>
  useAuth.getState().setSession(userId ?? null);
export const setActiveTenant = (t: string | null) =>
  useAuth.getState().setActiveTenant(t);
export const clearSession = () => useAuth.getState().clear();
